import { createSlice, Slice } from '@reduxjs/toolkit';
import { initPaginatedListData } from 'modules/shared/constants';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import {
	ILiveSessionCard,
	ILiveSessionCardBase,
	ILiveSessionCardListItem,
} from '../interfaces/LiveSessionCard';

export const LIVE_SESSION_CARD_KEY_IN_STORE = 'liveSessionCard';

export interface ILiveSessionCardState {
	isLoading: boolean;

	cards: IPaginatedData<ILiveSessionCardListItem>;
	searchCardsAutoComplete: {
		isLoading: boolean;
		result: ILiveSessionCardBase[];
	};
	deleteCard: IReqStatus;
	createCard: IReqStatus;
	updateCard: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ILiveSessionCard;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
}

const initialState: ILiveSessionCardState = {
	isLoading: false,
	cards: initPaginatedListData,

	searchCardsAutoComplete: {
		isLoading: false,
		result: [],
	},
	deleteCard: {
		error: false,
		success: false,
	},
	createCard: {
		error: false,
		success: false,
	},
	updateCard: {
		itemToUpdate: {
			isLoading: false,
			status: {
				error: false,
				success: false,
			},
		},
		status: {
			error: false,
			success: false,
		},
	},
};

const LiveSessionCardSlice: Slice = createSlice({
	name: LIVE_SESSION_CARD_KEY_IN_STORE,
	initialState,
	reducers: {
		resetLiveSessionCardData: () => {
			return initialState;
		},

		getCards: (state: ILiveSessionCardState) => {
			state.isLoading = true;
		},
		getCardsSuccess: (state: ILiveSessionCardState, action) => {
			state.isLoading = false;
			state.cards = action.payload;
		},
		getCardsFailure: (state: ILiveSessionCardState) => {
			state.isLoading = false;
			state.cards = initialState.cards;
		},

		searchCards: (state: ILiveSessionCardState) => {
			state.searchCardsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchCardsResult: (state: ILiveSessionCardState, action) => {
			state.searchCardsAutoComplete = {
				isLoading: false,
				result: action.payload.cards,
			};
		},
		resetSearchCards: (state: ILiveSessionCardState) => {
			state.searchCardsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		deleteCard: (state: ILiveSessionCardState) => {
			state.isLoading = true;
		},
		deleteCardSuccess: (state: ILiveSessionCardState) => {
			state.isLoading = false;
			state.deleteCard = {
				error: false,
				success: true,
			};
		},
		deleteCardFailure: (state: ILiveSessionCardState) => {
			state.isLoading = false;
			state.deleteCard = {
				error: true,
				success: false,
			};
		},
		resetDeleteCard: (state: ILiveSessionCardState) => {
			state.deleteCard = {
				error: false,
				success: false,
			};
		},

		createCard: (state: ILiveSessionCardState) => {
			state.isLoading = true;
		},
		createCardSuccess: (state: ILiveSessionCardState) => {
			state.createCard = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createCardFailure: (state: ILiveSessionCardState) => {
			state.createCard = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getCardToUpdate: (state: ILiveSessionCardState) => {
			state.updateCard.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getCardToUpdateSuccess: (state: ILiveSessionCardState, action) => {
			state.updateCard.itemToUpdate = {
				isLoading: false,
				data: action.payload.card,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getCardToUpdateFailure: (state: ILiveSessionCardState) => {
			state.updateCard.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateCard: (state: ILiveSessionCardState) => {
			state.isLoading = true;
		},
		updateCardSuccess: (state: ILiveSessionCardState) => {
			state.updateCard.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateCardFailure: (state: ILiveSessionCardState) => {
			state.updateCard.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
	},
});

export const {
	resetLiveSessionCardData,

	getCards,
	getCardsSuccess,
	getCardsFailure,

	searchCards,
	setSearchCardsResult,
	resetSearchCards,

	deleteCard,
	deleteCardSuccess,
	deleteCardFailure,
	resetDeleteCard,

	createCard,
	createCardSuccess,
	createCardFailure,

	getCardToUpdate,
	getCardToUpdateSuccess,
	getCardToUpdateFailure,

	updateCard,
	updateCardSuccess,
	updateCardFailure,
} = LiveSessionCardSlice.actions;

export const LiveSessionCardStateSelector = (
	state: RootStateOrAny,
): ILiveSessionCardState => state[LIVE_SESSION_CARD_KEY_IN_STORE];

export default LiveSessionCardSlice;
